'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import {
  useOptionalPrintingActions,
  useOptionalPrintingState,
} from '@/features/printing/presentation/providers/printing-provider';
import { printTotemTicket } from '@/features/printing/presentation/utils/print-tickets';
import {
  createEventDispatcher,
  type TakeOrderFlowDeps,
  type TakeOrderFlowDispatcher,
} from '@/features/totems/presentation/providers/web-totem/take-order-flow-router';
import { useTotemCartState, useTotemCatalog } from '@/features/totems/presentation/providers/web-totem/totem-provider';

const TakeOrderFlowCtx = createContext<TakeOrderFlowDispatcher | null>(null);

/** Despachador de eventos del take order. Solo lo monta el mediador. */
export function useTakeOrderFlowEvents(): TakeOrderFlowDispatcher {
  const dispatch = useContext(TakeOrderFlowCtx);
  if (!dispatch) {
    throw new Error('useTakeOrderFlowEvents debe usarse dentro de TakeOrderFlowProvider');
  }
  return dispatch;
}

/**
 * Mediador de los tres flujos del take order (carrito, pago e impresión).
 *
 * No tiene estado propio: expone un `dispatch` estable que traduce eventos del pago en
 * comandos sobre el carrito y la impresión. Debe montarse **dentro** del provider del carrito
 * (`TotemProvider` / `CartaProvider`) y por fuera del checkout.
 *
 * Las dependencias se leen a través de un ref: el mediador se re-renderiza cuando cambia el
 * carrito o las impresoras, pero `children` es el mismo elemento y no se vuelve a renderizar.
 */
export function TakeOrderFlowProvider({ children }: { children: ReactNode }) {
  const { cart } = useTotemCartState();
  const { totem } = useTotemCatalog();
  const printingActions = useOptionalPrintingActions();
  const printingState = useOptionalPrintingState();

  const depsRef = useRef<TakeOrderFlowDeps>({
    clearCart: () => undefined,
    printing: null,
    isPrintingReady: false,
    totemPrinter: null,
    printTotemDriver: (order, target) => printTotemTicket(target, order),
  });

  const printer = totem?.printer ?? null;

  useEffect(() => {
    depsRef.current = {
      clearCart: cart.clearCart,
      printing: printingActions,
      isPrintingReady: !(printingState?.isLoadingPrinters ?? false),
      totemPrinter: printer ? { ip: printer.ip, port: printer.port } : null,
      printTotemDriver: (order, target) => printTotemTicket(target, order),
    };
  });

  // El dispatcher es estable y lee las dependencias del ref en el momento de despachar (nunca durante el render).
  // eslint-disable-next-line react-hooks/refs -- el ref solo se lee dentro del handler de eventos
  const dispatch = useMemo(() => createEventDispatcher(() => depsRef.current), []);

  return <TakeOrderFlowCtx.Provider value={dispatch}>{children}</TakeOrderFlowCtx.Provider>;
}
